import React, {useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import pick from 'lodash/pick';

import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';

import config from '../../config/config';
import { useGetPayersQuery } from '../../redux/services/payerApi';
import CustomInput from '../common/CustomInput';

const getOptions = payers => (
  (payers || []).map(p => ({
    label: p.name,
    value: p.id,
  }))
);

const handleTextChange = ({setTextInput, setQuery, limit = 100}) => e => {
  const value = get(e, 'target.value', '');
  setTextInput(value);
  setQuery({name: value, limit});
};

const FindPayerInput = props => {
  const {onSubmit, limit} = props;
  const [textInput, setTextInput] = useState('');
  let initialQuery = {limit};
  if (props.defaultValue || props.defaultPayerId) {
    initialQuery.id = props?.defaultValue || props?.defaultPayerId;
  }
  const [query, setQuery] = useState(initialQuery);
  const payersQuery = useGetPayersQuery(
    {...query},
    {skip: !query?.name && !query?.id}
  );
  const isLoading = payersQuery.isLoading;
  const textChangeHandler = handleTextChange({limit, setTextInput, setQuery});
  const debouncedTextChangeHandler = useCallback(
    debounce(textChangeHandler, 500),
    [textChangeHandler]
  );
  const payers = (get(payersQuery, 'data.data') || []);
  const options = textInput ? getOptions(payers) : [];
  if (props.defaultValue && !payers.length) return <></>
  return (
    <CustomInput {...props}>
      <Autocomplete
        type='text'
        style={{minWidth: props.minWidth || null, maxWidth: props.maxWidth || null}}
        options={options}
        filterOptions={x => x}
        loading={isLoading}
        defaultValue={
          props.defaultValue 
          && payers.map(p => ({...p, label: p.name, value: p.id}))
          .find(p => props?.defaultValue === p.value)
        }
        onChange={(e,val,reason) => {
          if (reason === 'selectOption') {
            e.target = {...(e.target || {}), value: val.value};
            if (val.value) return onSubmit(e, {payerId: val.value});
            if (val.id) return onSubmit(e, {payerId: val.id});
          }
          debouncedTextChangeHandler(e);
        }}
        renderInput={params => (
          <TextField
            placeholder={props.placeholder}
            onChange={debouncedTextChangeHandler}
            {...params}
            defaultValue={props?.defaultValue || props?.defaultPayerId}
            sx={props.sx}
            size={props.size}
            fullWidth={props.fullWidth}
            className={`${props.rounded && 'custom-text-field-rounded'}`}
            minWidth={props.minWidth}
            InputProps={{
              ...params.InputProps,
              autoFocus: props.autoFocus,
              startAdornment: <InputAdornment position="start">
                <SearchIcon style={{color: config().colors.primary, fontSize: '1.25rem', marginLeft: '4px'}} />
              </InputAdornment>,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        {...pick(
          props, 
          'id', 'placeholder', 'freeSolo', 'disableClearable', 'autoFocus', 'onSubmit',
          'size',
          'fullWidth',
          'minWidth'
        )}
          />
    </CustomInput>
  );

};

FindPayerInput.defaultProps = {
  autoFocus: false,
  defaultPayerId: null,
  disableClearable: false,
  freeSolo: true,
  id: 'FindPayerInput.Autocomplete',
  placeholder: 'Search Insurers',
  onSubmit: (e, {payerId}) => console.log('FindPayerInput.onSubmit', payerId),
  size: 'small',
  minHeight: '28px',
  fullWidth: true,
  padding: 0.5,
  limit: 100,
};

FindPayerInput.propTypes = {
  filter: PropTypes.object,
  autoFocus: PropTypes.bool,
  defaultPayerId: PropTypes.string,
  disableClearable: PropTypes.bool,
  freeSolo: PropTypes.bool,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  onSubmit: PropTypes.func,
  size: PropTypes.string,
  variant: PropTypes.string, // chip variant
};

export default FindPayerInput;